import { z } from 'zod';

import { VenueSlotWritableShape } from '../venue-slot-create/venue-slot-create';

/**
 * A partial edit of one slot.
 *
 * Partial for two callers: the day panel's inline status Switch, which sends
 * `{ status }` and nothing else (W7), and a re-timing that must not restate a
 * capacity nobody changed.
 *
 * **It carries no cross-field check, on purpose.** `endsAt` after `startsAt` is
 * only answerable where both are present, and a patch that moves one end has
 * nothing to compare the other with; inferring the absent half from the stored
 * row *here* would put a database read inside a schema. `VenueSlotsService`
 * re-checks the ordering against the stored row merged with this patch, which is
 * the one place both values exist — and it is the same place the overlap and
 * booking guards run, so the three refusals are decided together rather than in
 * two layers that can disagree.
 *
 * **An empty body is refused.** `{}` would take the venue's row lock, write an
 * audit entry and change nothing — a trail that records a change nobody made is
 * worse than no entry, because it is the one somebody will later have to
 * explain. (`VenueUpdateSchema` accepts `{}` and gets away with it because its
 * only partial caller always sends `isActive`; this one is reachable from a form.)
 */
export const VenueSlotUpdateSchema = z
  .object(VenueSlotWritableShape)
  .partial()
  .strict()
  .refine((patch) => Object.keys(patch).length > 0, {
    message: 'Name at least one field to change.',
  });

export type VenueSlotUpdateRequest = z.infer<typeof VenueSlotUpdateSchema>;
